import React, { JSX, useContext, useMemo, useState } from "react";
import { PciDashboardScenarioTypeEnum } from "@app/projects/network-assessment/pci-dashboard/shared";
import { getScenarioDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";

interface PciDashboardFiltersOverviewProps {
  isDashboardDetailsView?: boolean;
  showLineOfBusiness?: boolean;
  showNewPatientClaims: boolean;
}

const PciDashboardFiltersOverview = (props: PciDashboardFiltersOverviewProps): JSX.Element => {
  const [displayedCounties, setDisplayedCounties] = useState<string[]>([]);
  const [displayedStates, setDisplayedStates] = useState<string[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  const accordionTitle: string | JSX.Element = useMemo(() => {
    if (pciDashboardFiltersContext.filtersLoading) {
      return (
        <span>
          Filters: <i className="fa fa-spinner fa-spin" />
        </span>
      );
    }

    let totalFilters = 0;

    if (!!pciDashboardFiltersContext.displayedFilters) {
      const { counties, states } = calculateServiceAreaSelections(pciDashboardFiltersContext.serviceAreas, pciDashboardFiltersContext.displayedFilters.countySSAs);
      setDisplayedCounties(counties);
      setDisplayedStates(states);

      totalFilters = counties.length +
        states.length +
        pciDashboardFiltersContext.displayedFilters.countyStatuses.length +
        pciDashboardFiltersContext.displayedFilters.specialtyGroupCodes.length +
        pciDashboardFiltersContext.displayedFilters.topProcedures.length +
        pciDashboardFiltersContext.displayedFilters.topConditions.length +(
          (props.showLineOfBusiness && pciDashboardFiltersContext.displayedFilters.lineOfBusiness) ? 1 : 0
        ) + (
          (props.showNewPatientClaims && pciDashboardFiltersContext.displayedFilters.newPatientClaims.length < 3) ?
          pciDashboardFiltersContext.displayedFilters.newPatientClaims.length : 0
        ) + (
          (props.isDashboardDetailsView && !!pciDashboardFiltersContext.displayedFilters.rankingRequestType) ? 1 : 0
        ) + (
          (props.isDashboardDetailsView ? !!pciDashboardFiltersContext.displayedFilters.scenario : !!pciDashboardFiltersContext.displayedFilters.compareScenario) ? 1 : 0
        ) + (
          (props.isDashboardDetailsView && !!pciDashboardFiltersContext.displayedFilters.providerNetworkType) ? 1 : 0
        ) + (
          (props.isDashboardDetailsView && (props.showLineOfBusiness ? !!pciDashboardFiltersContext.displayedFilters.claimsLabel : !!pciDashboardFiltersContext.displayedFilters.insightsCategory)) ? 1 : 0
        );
    }

    return `Filters: ${totalFilters} Applied`;
  }, [pciDashboardFiltersContext.displayedFilters, pciDashboardFiltersContext.filtersLoading]);

  const displayedFilters = useMemo(() => {
    if (!!pciDashboardFiltersContext.displayedFilters) {
      const providerNetworkType: JSX.Element = props.isDashboardDetailsView && (
        <span className="dashboard-filters-overview__filter">
          {capitalizeFirstLetter(pciDashboardFiltersContext.displayedFilters.providerNetworkType)}
        </span>
      );

      const labelOrScoreValue = props.showLineOfBusiness ? pciDashboardFiltersContext.displayedFilters.claimsLabel : pciDashboardFiltersContext.displayedFilters.insightsCategory;

      const labelOrScore: JSX.Element = (props.isDashboardDetailsView && !!labelOrScoreValue) && (
        <span className="dashboard-filters-overview__filter">
          {labelOrScoreValue}
        </span>
      );

      const rankingRequestType: JSX.Element = (props.isDashboardDetailsView && !!pciDashboardFiltersContext.displayedFilters.rankingRequestType) && (
        <span className="dashboard-filters-overview__filter">
          {capitalizeFirstLetter(pciDashboardFiltersContext.displayedFilters.rankingRequestType)}
        </span>
      );

      const scenarioValue = props.isDashboardDetailsView ? (
        pciDashboardFiltersContext.displayedFilters.scenario === PciDashboardScenarioTypeEnum.Original ?
          formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated) :
          getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.scenario)
      ) :
        getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario);

      const scenario: JSX.Element = !!scenarioValue && (
        <span className="dashboard-filters-overview__filter">
          {scenarioValue}
        </span>
      );

      const lineOfBusiness: JSX.Element = props.showLineOfBusiness && (
        <span className="dashboard-filters-overview__filter">
          {pciDashboardFiltersContext.displayedFilters.lineOfBusiness}
        </span>
      );

      const newPatientClaims: JSX.Element[] = props.showNewPatientClaims &&
        pciDashboardFiltersContext.displayedFilters.newPatientClaims.length === 3 ?
        [<span key="All" className="dashboard-filters-overview__filter">All</span> ]
        : pciDashboardFiltersContext.displayedFilters.newPatientClaims
          .sort((a, b) => b.localeCompare(a))
          .map(cf => (
          <span key={cf} className="dashboard-filters-overview__filter">
            {pciDashboardFiltersContext.newPatientClaimsFilters.find(x => x.key === cf)?.name}
          </span>
      ));

      const states: JSX.Element[] = displayedStates.map((state) => (
        <span key={state} className="dashboard-filters-overview__filter">
          {state}
        </span>
      ));

      const getCountyDisplay = (countySSA: string): string => {
        const county = pciDashboardFiltersContext.counties.find(x => x.countySSA === countySSA);

        if (!!county) {
          return `${county.countyName}, ${county.stateName}`;
        }
      }

      const counties: JSX.Element[] = displayedCounties.map((county) => (
        <span key={county} className="dashboard-filters-overview__filter">
          {`${getCountyDisplay(county)}`}
        </span>
      ));

      const countyStatuses: JSX.Element[] = pciDashboardFiltersContext.displayedFilters.countyStatuses.map((countyStatus) => (
        <span key={countyStatus} className="dashboard-filters-overview__filter">
          {countyStatus}
        </span>
      ));

      const specialties: JSX.Element[] = pciDashboardFiltersContext.displayedFilters.specialtyGroupCodes.map((sp) => (
        <span key={sp} className="dashboard-filters-overview__filter">
          {pciDashboardFiltersContext.specialties.find(x => x.key.specialtyGroupCode === sp)?.name}
        </span>
      ));

      const topProcedures: JSX.Element[] =
        pciDashboardFiltersContext.displayedFilters.topProcedures.length === 0
          ? [<span key="all" className="dashboard-filters-overview__filter">All</span>]
          : pciDashboardFiltersContext.displayedFilters.topProcedures.map((p) => (
              <span key={p} className="dashboard-filters-overview__filter">
              {pciDashboardFiltersContext.topProcedures.find(x => x.key.code === p)?.name}
              </span>
            ));

      const topConditions: JSX.Element[] =
        pciDashboardFiltersContext.displayedFilters.topConditions.length === 0
          ? [<span key="all" className="dashboard-filters-overview__filter">All</span>]
          : pciDashboardFiltersContext.displayedFilters.topConditions.map((p) => (
            <span key={p} className="dashboard-filters-overview__filter">
              {pciDashboardFiltersContext.topConditions.find(x => x.key.code === p)?.name}
            </span>
          ));

      return {
        counties,
        countyStatuses,
        labelOrScore,
        lineOfBusiness,
        newPatientClaims,
        providerNetworkType,
        rankingRequestType,
        scenario,
        specialties,
        states,
        topConditions,
        topProcedures
      };
    }
  }, [pciDashboardFiltersContext.displayedFilters, displayedCounties, displayedStates]);

  return (
    <div className="row">
      <div className="col-12">
        <AccordionContainer>
          <Accordion
            testId="pci-dashboard__filters-applied--accordion-panel"
            title={accordionTitle}
          >
            {pciDashboardFiltersContext.filtersLoading ? (
              <LoadingSpinner />
            ) : (
              <React.Fragment>
                {props.isDashboardDetailsView && (
                  <React.Fragment>
                    <div className="d-flex">
                      <span className="dashboard-filters-overview__label">Provider Network Type:</span>
                      <span>{displayedFilters?.providerNetworkType}</span>
                    </div>
                    {!!displayedFilters?.labelOrScore && (
                      <div className="d-flex">
                        <span className="dashboard-filters-overview__label">{props.showLineOfBusiness ? "Claims Volume Label" : "Score Type"}:</span>
                        <span>{displayedFilters?.labelOrScore}</span>
                      </div>
                    )}
                    {(!props.showLineOfBusiness && !!pciDashboardFiltersContext?.displayedFilters?.rankingRequestType) && (
                      <div className="d-flex">
                        <span className="dashboard-filters-overview__label">Ranking Request Type:</span>
                        <span>{displayedFilters?.rankingRequestType}</span>
                      </div>
                    )}
                  </React.Fragment>
                )}
                {!!displayedFilters?.scenario && (
                  <div className="d-flex">
                    <span className="dashboard-filters-overview__label">Scenario:</span>
                    <span>{displayedFilters?.scenario}</span>
                  </div>
                )}
                {props.showLineOfBusiness && (
                  <div className="d-flex">
                    <span className="dashboard-filters-overview__label">Line of Business:</span>
                    <span>{displayedFilters?.lineOfBusiness}</span>
                  </div>
                  )}
                {props.showNewPatientClaims && (
                    <div className="d-flex">
                      <span className="dashboard-filters-overview__label">New Patient Claims:</span>
                      <span>{displayedFilters?.newPatientClaims}</span>
                    </div>
                  )
                }
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">State - All Licensed Counties:</span>
                  <span>{displayedFilters?.states}</span>
                </div>
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">County:</span>
                  <span>{displayedFilters?.counties}</span>
                </div>
                {(!!pciDashboardFiltersContext.countyStatuses.length) && (
                  <div className="d-flex">
                    <span className="dashboard-filters-overview__label">County Statuses:</span>
                    <span>{displayedFilters?.countyStatuses}</span>
                  </div>
                )}
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">Specialty:</span>
                  <span>{displayedFilters?.specialties}</span>
                </div>
                {pciDashboardFiltersContext.topProcedures.length > 0 && (
                  <div className="d-flex">
                    <span className="dashboard-filters-overview__label">Procedures:</span>
                      <span>{displayedFilters?.topProcedures}</span>
                  </div>
                  )}
                {pciDashboardFiltersContext.topConditions.length > 0 && (
                  <div className="d-flex">
                      <span className="dashboard-filters-overview__label">Top Conditions:</span>
                      <span>{displayedFilters?.topConditions}</span>
                  </div>
                )}
              </React.Fragment>
            )}
          </Accordion>
        </AccordionContainer>
      </div>
    </div>
  );
}

export default PciDashboardFiltersOverview;